import { GLYPHS, MAP, MASCATE_ITEM, PALETTE, PING_LABELS, structureName, type PingKind } from "@eldrun/content";
import { BIOME, PLAN_SIDE, PLAN_SPACING, WORLD_HALF, WORLD_SIZE, type Poi } from "@eldrun/worldgen";

/** Paleta do doc 05 (base de cada bioma; o mar pela profundidade). */
const BIOME_RGB: Record<number, [number, number, number]> = {
  [BIOME.praia]: [0xe8, 0xd4, 0x9a],
  [BIOME.mangue]: [0x5e, 0x53, 0x40],
  [BIOME.mata]: [0x3e, 0x6b, 0x2f],
  [BIOME.cerrado]: [0xc9, 0xb2, 0x5a],
  [BIOME.serra]: [0x8a, 0x85, 0x78],
};
const SEA_SHALLOW: [number, number, number] = [0x5f, 0xd3, 0xc4];
const SEA_MID: [number, number, number] = [0x1f, 0x9f, 0xb5];
const SEA_DEEP: [number, number, number] = [0x0e, 0x4f, 0x7a];

/**
 * Imagem da ilha (doc 05): cada célula do plano (8 m) com a cor do bioma e sombreamento de relevo
 * (luz de noroeste); o mar raso, médio e fundo pela profundidade. Gerada no cliente pela seed, sem
 * tráfego. RGBA de PLAN_SIDE². Malha neon (F59; o único estilo desde a F73): em cinza, a luminância
 * de cada cor.
 */
export function paintIsland(plan: { heights: Float32Array; biome: Uint8Array }): Uint8ClampedArray<ArrayBuffer> {
  const n = PLAN_SIDE;
  const out = new Uint8ClampedArray(n * n * 4);
  const h = (ix: number, iz: number) => plan.heights[Math.min(n - 1, Math.max(0, iz)) * n + Math.min(n - 1, Math.max(0, ix))] ?? 0;
  for (let iz = 0; iz < n; iz++) {
    for (let ix = 0; ix < n; ix++) {
      const i = iz * n + ix;
      const height = plan.heights[i] ?? 0;
      const biome = plan.biome[i] ?? BIOME.mar;
      let rgb: [number, number, number];
      let shade = 1;
      if (biome === BIOME.mar || height < 0) rgb = height > -3 ? SEA_SHALLOW : height > -15 ? SEA_MID : SEA_DEEP;
      else {
        rgb = BIOME_RGB[biome] ?? BIOME_RGB[BIOME.mata]!;
        // Relevo: diferença de altura na diagonal noroeste–sudeste.
        shade = Math.max(0.6, Math.min(1.35, 1 + (h(ix - 1, iz - 1) - h(ix + 1, iz + 1)) * 0.035));
      }
      const luma = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) * shade;
      out[i * 4] = out[i * 4 + 1] = out[i * 4 + 2] = luma;
      out[i * 4 + 3] = 255;
    }
  }
  return out;
}

const cell = (v: number) => Math.round((v + WORLD_HALF) / PLAN_SPACING);

/**
 * Névoa de exploração (doc 06): o mapa se revela num raio de 60 m em volta de cada aliado (no
 * Mutirão, de todos; na Treta, só o próprio). Vive no cliente, pelas posições públicas.
 */
export class FogOfWar {
  readonly cells = new Uint8Array(PLAN_SIDE * PLAN_SIDE);
  private revealedCount = 0;
  /** Mudou desde a última vez que o mapa redesenhou a névoa. */
  dirty = true;

  reveal(x: number, z: number, radius: number = MAP.reveal): void {
    const r = Math.ceil(radius / PLAN_SPACING);
    const cx = cell(x);
    const cz = cell(z);
    for (let iz = Math.max(0, cz - r); iz <= Math.min(PLAN_SIDE - 1, cz + r); iz++) {
      for (let ix = Math.max(0, cx - r); ix <= Math.min(PLAN_SIDE - 1, cx + r); ix++) {
        const i = iz * PLAN_SIDE + ix;
        if (this.cells[i] || (ix - cx) ** 2 + (iz - cz) ** 2 > r * r) continue;
        this.cells[i] = 1;
        this.revealedCount++;
        this.dirty = true;
      }
    }
  }

  revealed(x: number, z: number): boolean {
    const ix = cell(x);
    const iz = cell(z);
    return ix >= 0 && iz >= 0 && ix < PLAN_SIDE && iz < PLAN_SIDE && this.cells[iz * PLAN_SIDE + ix] === 1;
  }

  /** Fração do mundo revelada (testes). */
  get fraction(): number {
    return this.revealedCount / this.cells.length;
  }
}

export type MarkerKind = "eu" | "aliado" | "ping" | "carranca" | "pedra" | "mascate" | "jangada" | "guardiao" | "cruz" | "redemoinho" | "chefe";

export interface MapMarker {
  /** Estável entre os redesenhos (a seleção da lista e do detalhe segue o mesmo ponto, F82). */
  id: string;
  kind: MarkerKind;
  x: number;
  z: number;
  label: string;
  /** Ping (F82): quem pingou e o tipo ("Perigo"), para o detalhe. */
  by?: string;
  note?: string;
  /** Símbolo (forma além da cor, para daltônicos) e cor. */
  symbol: string;
  color: string;
  /** Rumo (rad) para o próprio jogador. */
  yaw?: number;
}

export interface MapCircle {
  kind: "cerco" | "cerco-proximo";
  x: number;
  z: number;
  r: number;
}

export interface MapInput {
  self: { x: number; z: number; yaw: number } | undefined;
  /** `n`: o número do slot (1 a 4), o reforço da cor (doc 09 §2.6). */
  allies: readonly { name: string; color: string; n: number; x: number; z: number; dead: boolean }[];
  /** `color`: a de quem pingou (F75: cor = quem; o símbolo diz o tipo). */
  pings: readonly { x: number; z: number; kind: PingKind; name: string; color: string; id?: number }[];
  pois: readonly Poi[];
  revealed: (x: number, z: number) => boolean;
  raftKnown: boolean;
  guardiansKnown: boolean;
  raft: { x: number; z: number } | undefined;
  crosses: readonly { x: number; z: number; name: string }[];
  /** `id` (opcional): a chave do estado, para a seleção seguir o mesmo ponto quando ele anda. */
  whirlwinds: readonly { x: number; z: number; id?: string }[];
  bosses: readonly { x: number; z: number; name: string; id?: string }[];
  cerco: { x: number; z: number; radius: number; nextX: number; nextZ: number; nextRadius: number } | undefined;
  /** Hex da paleta → cor na tela (o modo daltônico troca); sem ela, o hex como está. */
  paint?: (hex: string) => string;
}

/** Marcadores sem dono são neutros (F75): o lugar se lê pelo símbolo; a cor fica para quem (jogador, chefe). */
const NEUTRO = PALETTE.neutro.hex;

const POI_MARKERS: Partial<Record<Poi["type"], Omit<MapMarker, "id" | "x" | "z">>> = {
  carranca: { kind: "carranca", label: structureName("carranca"), symbol: "♜", color: NEUTRO },
  pedra: { kind: "pedra", label: structureName("pedra-encanto"), symbol: "◈", color: NEUTRO },
  acampamento: { kind: "mascate", label: structureName(MASCATE_ITEM), symbol: "⌂", color: NEUTRO },
};

/**
 * O que o mapa mostra (doc 05 e 06): você e os aliados, pings, Carrancas, Pedras de Encanto e
 * mascates já descobertos (na névoa revelada), a Jangada (achada ou pelo mapa), os santuários dos
 * Guardiões (pelo Mapa dos Muiraquitãs), Cruzes, chefes, redemoinhos a menos de 60 m e, na Treta,
 * o Cerco de agora e o próximo.
 */
export function mapMarkers(input: MapInput): { markers: MapMarker[]; circles: MapCircle[] } {
  const markers: MapMarker[] = [];
  const paint = input.paint ?? ((hex: string) => hex);
  const at = (x: number, z: number) => `${Math.round(x)},${Math.round(z)}`;
  for (const p of input.pois) {
    const look = POI_MARKERS[p.type];
    if (look && input.revealed(p.x, p.z)) markers.push({ ...look, id: `${look.kind}:${at(p.x, p.z)}`, x: p.x, z: p.z });
    if (p.type === "guardiao" && input.guardiansKnown) markers.push({ id: `guardiao:${at(p.x, p.z)}`, kind: "guardiao", x: p.x, z: p.z, label: "Santuário", symbol: "✦", color: NEUTRO });
  }
  if (input.raft && input.raftKnown) markers.push({ id: "jangada", kind: "jangada", x: input.raft.x, z: input.raft.z, label: "Barco", symbol: "⛵", color: NEUTRO });
  for (const c of input.crosses) markers.push({ id: `cruz:${at(c.x, c.z)}`, kind: "cruz", x: c.x, z: c.z, label: `${structureName("cruz")} de ${c.name}`, symbol: "⚰", color: NEUTRO });
  input.bosses.forEach((b, i) => markers.push({ id: `chefe:${b.id ?? i}`, kind: "chefe", x: b.x, z: b.z, label: b.name, symbol: GLYPHS.coroa.char, color: paint(PALETTE.chefe.hex) }));
  if (input.self) {
    const self = input.self;
    input.whirlwinds.forEach((w, i) => {
      if (Math.hypot(w.x - self.x, w.z - self.z) < MAP.whirlwinds) markers.push({ id: `redemoinho:${w.id ?? i}`, kind: "redemoinho", x: w.x, z: w.z, label: "Redemoinho", symbol: "@", color: NEUTRO });
    });
  }
  for (const p of input.pings) {
    const look = PING_LABELS[p.kind];
    markers.push({ id: p.id !== undefined ? `ping:${p.id}` : `ping:${p.name}:${at(p.x, p.z)}`, kind: "ping", x: p.x, z: p.z, label: `${p.name}: ${look.label}`, symbol: look.symbol, color: p.color, by: p.name, note: look.label });
  }
  for (const a of input.allies) markers.push({ id: `aliado:${a.n}`, kind: "aliado", x: a.x, z: a.z, label: a.dead ? `${a.name} (caído)` : a.name, symbol: a.dead ? "✕" : String(a.n), color: a.color });
  if (input.self) markers.push({ id: "eu", kind: "eu", x: input.self.x, z: input.self.z, label: "Você", symbol: "▲", color: NEUTRO, yaw: input.self.yaw });
  const circles: MapCircle[] = [];
  const c = input.cerco;
  if (c && c.radius > 0) {
    circles.push({ kind: "cerco", x: c.x, z: c.z, r: c.radius });
    if (c.nextRadius < c.radius - 0.5) circles.push({ kind: "cerco-proximo", x: c.nextX, z: c.nextZ, r: c.nextRadius });
  }
  return { markers, circles };
}

/** Zoom e arrasto do mapa (doc 06: 0,25 a 6×). Com zoom 1, o mundo inteiro cabe no menor lado. */
export class MapView {
  cx = 0;
  cz = 0;
  zoom = 1.5;

  scale(width: number, height: number): number {
    return (this.zoom * Math.min(width, height)) / WORLD_SIZE;
  }

  toScreen(x: number, z: number, width: number, height: number): { x: number; y: number } {
    const s = this.scale(width, height);
    return { x: (x - this.cx) * s + width / 2, y: (z - this.cz) * s + height / 2 };
  }

  toWorld(sx: number, sy: number, width: number, height: number): { x: number; z: number } {
    const s = this.scale(width, height);
    return { x: (sx - width / 2) / s + this.cx, z: (sy - height / 2) / s + this.cz };
  }

  /** Zoom em volta de um ponto da tela (o ponto do mundo sob o cursor fica onde está). */
  zoomAt(factor: number, sx: number, sy: number, width: number, height: number): void {
    const before = this.toWorld(sx, sy, width, height);
    this.zoom = Math.max(MAP.zoomMin, Math.min(MAP.zoomMax, this.zoom * factor));
    const after = this.toWorld(sx, sy, width, height);
    this.cx += before.x - after.x;
    this.cz += before.z - after.z;
  }

  pan(dx: number, dy: number, width: number, height: number): void {
    const s = this.scale(width, height);
    this.cx = Math.max(-WORLD_HALF, Math.min(WORLD_HALF, this.cx - dx / s));
    this.cz = Math.max(-WORLD_HALF, Math.min(WORLD_HALF, this.cz - dy / s));
  }
}
